import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, MapPin, MessageSquare } from "lucide-react";
import { Logo } from "./Logo";
import { BIZ, NAV } from "../data/site";
import { GafCertified } from "./GafBadge";

const TopBar = () => (
  <div className="bg-slate-950 text-slate-200" data-testid="top-bar">
    <div className="wrap flex h-10 items-center justify-between text-xs sm:text-sm">
      <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-amber-400" /><span className="sm:hidden">Milwaukee, WI</span><span className="hidden sm:inline">Milwaukee · GAF Certified™ · BBB A Rated · 5.0★ on Google</span></span>
      <div className="flex items-center gap-5">
        <a href={BIZ.sms} className="hidden items-center gap-2 hover:text-amber-400 sm:flex" data-testid="topbar-text-link"><MessageSquare className="h-3.5 w-3.5" />Text us</a>
        <a href={BIZ.tel} className="flex items-center gap-2 py-2.5 font-semibold text-white hover:text-amber-400" data-testid="topbar-call-link"><Phone className="h-3.5 w-3.5 text-amber-400" />{BIZ.phone}</a>
      </div>
    </div>
  </div>
);

const MobileMenu = ({ close }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="dark-ctx fixed inset-0 z-[60] flex flex-col bg-slate-950 px-6 pb-10 pt-6 text-white lg:hidden" data-testid="mobile-menu">
    <div className="flex items-center justify-between">
      <Logo light onClick={close} />
      <button onClick={close} className="rounded-full border border-white/20 p-3" aria-label="Close menu" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
    </div>
    <nav className="mt-12 flex flex-col">
      {[{ to: "/", label: "Home" }, ...NAV].map((n, i) => (
        <motion.div key={n.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
          <Link to={n.to} onClick={close} className="font-display block border-b border-white/10 py-4 text-4xl font-bold uppercase" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</Link>
        </motion.div>
      ))}
    </nav>
    <div className="mt-auto grid gap-3">
      <a href={BIZ.tel} className="btn-amber justify-center" data-testid="mobile-menu-call"><Phone className="h-4 w-4" />Call {BIZ.phone}</a>
      <Link to="/get-quote" onClick={close} className="btn-ghost-light justify-center" data-testid="mobile-menu-estimate">Get a Free Quote</Link>
    </div>
  </motion.div>
);

const HeaderBadges = () => (
  <div className="flex min-w-0 flex-1 justify-center px-2 lg:hidden" data-testid="header-badges">
    <div className="grid grid-cols-2 items-center justify-items-center gap-x-2 gap-y-1.5 sm:gap-x-3">
      <GafCertified className="col-span-2" />
      <img src="/img/badges/abc.svg" alt="ABC Supply" className="h-5 w-auto sm:h-6" data-testid="header-badge-abc" />
      <img src="/img/badges/homeadvisor.svg" alt="HomeAdvisor" className="h-3 w-auto sm:h-4" data-testid="header-badge-homeadvisor" />
    </div>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <TopBar />
      <header className={`sticky top-0 z-50 border-b transition-[background-color,box-shadow] duration-300 ${scrolled ? "border-slate-200 bg-white/95 shadow-sm backdrop-blur-xl" : "border-transparent bg-white"}`} data-testid="site-header">
        <div className="wrap flex h-20 items-center justify-between">
          <Logo />
          <HeaderBadges />
          <nav className="hidden items-center gap-6 lg:flex xl:gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `nav-link text-sm font-semibold uppercase tracking-wider text-slate-800 ${isActive ? "is-active" : ""}`}>{n.label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={BIZ.tel} className="hidden items-center gap-2 whitespace-nowrap font-display text-xl font-bold text-slate-900 md:flex lg:hidden xl:flex" data-testid="header-call-button"><Phone className="h-4 w-4" />{BIZ.phone}</a>
            <span className="hidden sm:block"><Link to="/get-quote" className="btn-amber whitespace-nowrap" data-testid="header-estimate-button">Free Quote</Link></span>
            <button onClick={() => setOpen(true)} className="rounded-full border border-slate-300 p-3 lg:hidden" aria-label="Open menu" data-testid="mobile-menu-open"><Menu className="h-5 w-5" /></button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu close={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
